import React, { useEffect, useState } from "react";
import DialogModal from "../../../../atoms/dialog";
import { agenda_response, ModalStates } from "../../../project-detail/project-detail-card/helper";
import { AgendaItem } from "../../../../pages/Projects/types";
import { useProjectPageContext } from "../../../../pages/Projects/helper";
import { getAgenda } from "../../../project-detail/project-navbar/helper";
import { FormProvider, useForm } from "react-hook-form";
import { Box, CircularProgress, createTheme } from "@mui/material";
import { agendaContainer } from "../project-agenda-description/styles";
import Fields from "../project-agenda-description/save-agenda-response/fields";
import { ThemeProvider } from "@emotion/react";
import { defaultFormTheme } from "../../../../atoms/defaultFormTheme";
import CustomBtnFilled from "../../../../atoms/form-molecules/CustomBtnFilled";
import { useSnackbar } from "notistack";
import { getAgendaData, shareAgendaHandler } from "../helper";

type Props = {
  handleClose: () => void;
  isOpen: boolean;
  fk_agenda: ModalStates["isAgendaDescription"]["fk_agenda"];
  projectId: string | null;
  pe_id: number | null;
  isAgendaIdChanged: boolean;
};

function ShareAgendaPreview({
  handleClose,
  isOpen,
  fk_agenda,
  pe_id,
  projectId,
  isAgendaIdChanged
}: Props) {
  const [quesArr, setQuesArr] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isSharing, setIsSharing] = useState<boolean>(false);

  const methods = useForm<{ agenda: agenda_response[] }>({
    defaultValues: { agenda: [] },
  });
  const { enqueueSnackbar } = useSnackbar();
  const { projectAgendaData, setProjectAgendaData } = useProjectPageContext();

  const refectProjectAgendaData = async () => {
    if (projectId) {
      await getAgendaData(setProjectAgendaData, projectId)
    }
  }
  const agendaMatchedPE = projectAgendaData?.agenda?.find(
    (agenda: AgendaItem) => agenda.id === pe_id
  );
  useEffect(() => {
    const fetchAgendaQuestions = async () => {
      if (isOpen && projectAgendaData?.applicable_agenda_id) {
        setLoading(true);
        try {
          await getAgenda(setQuesArr, projectAgendaData.applicable_agenda_id);
        } catch (err) {
          console.error('Error fetching agenda questions', err);
          setQuesArr([]);
        } finally {
          setLoading(false);
        }
      } else {
        setQuesArr([]);
      }
    };

    fetchAgendaQuestions();
  }, [isOpen, projectAgendaData?.applicable_agenda_id]);

  useEffect(() => {
    methods.reset({ agenda: [] });
  }, [pe_id]);

  useEffect(() => {
    if (quesArr?.length > 0) {
      const responses = agendaMatchedPE?.agenda_responses ?? [];
      const merged = quesArr.map((question: string) => {
        const match = responses.find((r: agenda_response) => r.question === question);
        return {
          question,
          answer: match?.answer ?? null,
          expert_note: match?.expert_note ?? null,
        };
      });
      methods.reset({ agenda: merged });
    }
  }, [quesArr, agendaMatchedPE]);

  const agendaList = methods.getValues("agenda");
  const defaultTheme = createTheme(defaultFormTheme);

  const handleShareAgenda = async () => {
    if (!pe_id) return;

    setIsSharing(true);
    try {
      await shareAgendaHandler(
        pe_id,
        enqueueSnackbar,
        handleClose,
        refectProjectAgendaData
      );

    } catch (error) {
      console.error("Error sharing agenda:", error);
      enqueueSnackbar("Failed to share agenda", { variant: "error" });
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <DialogModal isOpen={isOpen} handleClose={handleClose} title={"Agenda Preview"}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ flex: 1, overflow: 'auto', pb: 6 }}>
          <FormProvider {...methods}>
            <ThemeProvider theme={defaultTheme}>
              <Box component="form" noValidate sx={{ ...agendaContainer, pt: 2, mb: 1 }}>
                {loading ? (
                  <h1 className="text-center">Loading...</h1>
                ) : agendaList.length > 0 ? (
                  agendaList.map((agendaObj, idx) => (
                    <Fields
                      key={agendaObj.question + idx}
                      agendaObj={agendaObj}
                      index={idx}
                      isPreviewOnly
                      isLast={idx === agendaList.length - 1}
                    />
                  ))
                ) : (
                  <h1 className="text-center">No agenda exists</h1>
                )}
              </Box>
            </ThemeProvider>
          </FormProvider>
        </Box>
        <Box sx={{
          position: 'absolute',
          bottom: 16,
          right: 24,
          backgroundColor: 'background.paper',
          zIndex: 2
        }}>
          <CustomBtnFilled
            label={isSharing ? "Sharing..." : "Share"}
            variant="outlined"
            disabled={isSharing || loading || !pe_id}
            onClick={handleShareAgenda}
            startIcon={isSharing ? <CircularProgress size={20} color="inherit" /> : null}
          />
        </Box>
      </Box>
    </DialogModal>
  );
}

export default ShareAgendaPreview;
